import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaUtensils, FaTree, FaShuttleVan } from 'react-icons/fa';
import { MdKitchen, MdLocalLaundryService, MdOutdoorGrill, MdLocalGasStation } from 'react-icons/md';
import { GiFlowerPot, GiWashingMachine } from 'react-icons/gi';
import { BiDish } from 'react-icons/bi';

const PropertyHighlights = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const highlights = [
    {
      icon: <MdKitchen />,
      title: "Fully Equipped Kitchen",
      description: "Complete kitchen setup for your cooking needs",
      color: "from-orange-400 to-orange-600"
    },
    {
      icon: <GiWashingMachine />,
      title: "Common Wash Area",
      description: "Convenient washing facilities for guests",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: <MdLocalLaundryService />,
      title: "Laundry Services",
      description: "Professional laundry services available",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: <MdLocalGasStation />,
      title: "EV Charging Point",
      description: "Electric vehicle charging facilities",
      color: "from-green-400 to-green-600"
    },
    {
      icon: <FaUtensils />,
      title: "Home Cooked Meals",
      description: "Delicious home-style meals on request",
      color: "from-red-400 to-red-600"
    },
    {
      icon: <FaTree />,
      title: "Garden Sit-out Spaces",
      description: "Relaxing outdoor seating in lush gardens",
      color: "from-emerald-400 to-emerald-600"
    },
    {
      icon: <GiFlowerPot />,
      title: "Terrace Garden",
      description: "Beautiful rooftop garden for peaceful moments",
      color: "from-teal-400 to-teal-600"
    },
    {
      icon: <FaShuttleVan />,
      title: "Inter-city Shuttle Service",
      description: "Convenient shuttle service on request",
      color: "from-indigo-400 to-indigo-600"
    }
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ rotate: 0 }}
            animate={inView ? { rotate: 360 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            className="inline-block mb-4"
          >
            <GiFlowerPot className="text-accent-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Property Highlights
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover what makes Navyaa special - a perfect blend of nature, comfort, and modern amenities
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {highlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.03 }}
              className="group relative bg-gradient-to-br from-white to-primary-50 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all cursor-pointer overflow-hidden"
            >
              {/* Background Gradient on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${highlight.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              {/* Icon */}
              <motion.div
                className={`text-5xl mb-4 bg-gradient-to-br ${highlight.color} bg-clip-text text-transparent`}
                whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.2 }}
                transition={{ duration: 0.5 }}
              >
                {highlight.icon}
              </motion.div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                {highlight.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {highlight.description}
              </p>

              {/* Decorative Element */}
              <div className="absolute -bottom-2 -right-2 w-20 h-20 bg-primary-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1 }}
          className="text-center mt-12"
        >
          <a href="#rooms" className="btn-primary inline-block">
            Explore Our Rooms
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PropertyHighlights;
